// Full-page states the route guards show while the account can't open the console yet (WEB_PLAN.md §3.1):
// - loading: restoring the stored session or reading `my_memberships`;
// - can't check: memberships couldn't be read (offline, server trouble), with Try again (iOS TeamAccountPendingCard);
// - paused: the account's only membership is suspended ("Your access was paused").
// All of them offer Sign out, so nobody is stuck on a screen they can't leave.

import { CirclePause, CloudOff, WifiOff } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { accountLabel } from '@/auth/accountText';
import { useAuth } from '@/auth/AuthProvider';
import { companyDisplayName } from '@/auth/memberships';
import { AuthHeader, AuthPage, AuthCardBody } from './AuthCard';
import { Caption, ErrorText, SecondaryButton, Spinner } from './ui';

export function FullPageLoading({ label }: { label?: string }) {
  const { user } = useAuth();
  const text = label ?? (user ? 'Checking your team membership…' : 'Loading…');
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-4 dark:bg-slate-950" role="status" aria-live="polite">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--gt-navy)] text-base font-black text-white shadow-sm">
        GT
      </span>
      <div className="flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400">
        <Spinner className="h-4 w-4 text-[var(--gt-orange)]" />
        <span>{text}</span>
      </div>
    </div>
  );
}

/** Try again + Sign out, shared by the problem screens. */
function GateActions({ retryTitle, children }: { retryTitle: string; children?: ReactNode }) {
  const { refreshMemberships, signOut } = useAuth();
  const [retrying, setRetrying] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const working = retrying || signingOut;
  return (
    <>
      {children}
      <SecondaryButton
        tone="navy"
        busy={retrying}
        busyLabel="Checking…"
        disabled={working}
        onClick={async () => {
          setRetrying(true);
          try {
            await refreshMemberships();
          } finally {
            setRetrying(false);
          }
        }}
      >
        {retryTitle}
      </SecondaryButton>
      <SecondaryButton
        busy={signingOut}
        busyLabel="Signing out…"
        disabled={working}
        onClick={async () => {
          setSigningOut(true);
          try {
            await signOut();
          } finally {
            setSigningOut(false);
          }
        }}
      >
        Sign out
      </SecondaryButton>
    </>
  );
}

/** Memberships couldn't be read: say why, Try again (also automatic when the browser comes back online). */
export function MembershipRetryScreen() {
  const { error, user } = useAuth();
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const account = accountLabel(user);
  return (
    <AuthPage>
      <AuthHeader
        icon={offline ? <WifiOff className="h-6 w-6" aria-hidden /> : <CloudOff className="h-6 w-6" aria-hidden />}
        title={offline ? "You're offline" : "Can't reach the team server"}
        subtitle="Your account is signed in, but we couldn't check your team membership."
      />
      <AuthCardBody>
        {error && <ErrorText>{error}</ErrorText>}
        {account && <Caption>Signed in as {account}</Caption>}
        <GateActions retryTitle="Try again" />
      </AuthCardBody>
    </AuthPage>
  );
}

/** The only membership is suspended: an admin paused this account's access. */
export function AccessPausedScreen() {
  const { memberships, user, error } = useAuth();
  const suspended = memberships.find((m) => m.status === 'suspended');
  const company = suspended ? companyDisplayName(suspended) : 'your company';
  const account = accountLabel(user);
  return (
    <AuthPage>
      <AuthHeader
        icon={<CirclePause className="h-6 w-6" aria-hidden />}
        title="Your access was paused"
        subtitle={`Your access to ${company} was paused by an admin.`}
      />
      <AuthCardBody>
        <p className="text-sm text-slate-700 dark:text-slate-300">
          Ask an admin of {company} to turn your access back on, then check again. Your account and the team's data stay
          on the server.
        </p>
        {error && <ErrorText>{error}</ErrorText>}
        {account && <Caption>Signed in as {account}</Caption>}
        <GateActions retryTitle="Check again" />
      </AuthCardBody>
    </AuthPage>
  );
}
